<template>
    <!-- The custom input -->
    <div class="custom-input" v-bind:class="{ toggle: type === 'toggle', colorPicker: type === 'colorpicker' }">

        <label :title="label" class="label" :for="name">{{label}}</label>

        <!-- FOR TEXTBOX -->
        <textbox
                v-if="type === 'text'"
                ref="text"
                v-validate="validationRules"
                :name="name"
                :placeholder="placeholder"
                @blur="showErrors = (localErrorMode === 'auto' ? true : showErrors)"
                @input="showErrors = (localErrorMode === 'auto' ? false : showErrors)"
                v-model="newValue"
                :maxlength="maxlength"
                :autocomplete="autocomplete"
        />

        <!-- FOR PASSWORD -->
        <password
                v-if="type === 'password'"
                ref="password"
                v-validate="validationRules"
                :name="name"
                :placeholder="placeholder"
                @blur="showErrors = (localErrorMode === 'auto' ? true : showErrors)"
                @input="showErrors = (localErrorMode === 'auto' ? false : showErrors)"
                v-model="newValue"
        />

        <!-- FOR TEXTAREA -->
        <text-area
                v-if="type === 'textarea'"
                ref="textarea"
                v-validate="validationRules"
                :name="name"
                :placeholder="placeholder"
                @blur="showErrors = (localErrorMode === 'auto' ? true : showErrors)"
                @input="showErrors = (localErrorMode === 'auto' ? false : showErrors)"
                v-model="newValue"
                :minHeight="minHeight"
        />

        <!-- FOR UPLOAD -->
        <upload
                v-if="type === 'upload'"
                ref="upload"
                v-validate="validationRules"
                :name="name"
                :placeholder="placeholder"
                @blur="showErrors = (localErrorMode === 'auto' ? true : showErrors)"
                @input="showErrors = (localErrorMode === 'auto' ? false : showErrors)"
                :accept="acceptExtensionsString"
                v-model="newValue"
        />

        <!-- FOR DISPLAYING ONLY -->
        <display
                v-if="type === 'display'"
                ref="display"
                :name="name"
                :placeholder="placeholder"
                v-model="newValue"
        />

        <!-- FOR DISPLAYNIG ONLY - TEXTAREA -->
        <display-area
                v-if="type === 'display-area'"
                ref="display-area"
                :name="name"
                :placeholder="placeholder"
                v-model="newValue"
        />

        <!-- FOR DISPLAYING SENSITIVE DATA (SECRET) (OLD) -->
        <display-secret
                v-if="type === 'secret'"
                ref="secret"
                :name="name"
                :placeholder="placeholder"
                v-model="newValue"
                :showSecret="showSecret"
                @toggleSecret="$emit('toggleSecret')"
        />

        <!-- FOR DISPLAYING SENSITIVE DATA (SECRET) (NEW) -->
        <display-secret-small
            v-if="type === 'secret-small'"
            ref="secretSmall"
            :name="name"
            v-model="newValue"
            :showSecret="showSecret"
            @toggleSecret="$emit('toggleSecret')"
        />

        <!-- FOR TOGGLE -->
        <toggle
                v-if="type === 'toggle'"
                ref="toggle"
                :name="name"
                v-model="newValue"
                :disabled="disabled"
                @change="$emit('change', $event)"
        />

        <!-- FOR DROPDOWN -->
        <dropdown
                v-if="type === 'dropdown'"
                ref="dropdown"
                :name="name"
                v-model="newValue"
                :items="items"
                @input="$emit('input', value)"
                :disabled="disabled"
        />

        <!-- Color picker -->
        <colorpicker
                v-if="type === 'colorpicker'"
                ref="colorpicker"
                :name="name"
                v-model="newValue"
        />

        <!-- Color buttons -->
        <colorbuttons
                v-if="type === 'colorbuttons'"
                ref="colorbuttons"
                :name="name"
                v-model="newValue"
                :colors="colors"
        />

        <datetime
                v-if="type === 'datetime'"
                ref="datetime"
                v-validate="validationRules"
                :name="name"
                :input-id="id"
                v-model="newValue"
                @input="$emit('input', value)"
                class="datetime"
                input-class="datetime-input"
                type="datetime"
                :placeholder="placeholder"
        />

        <datetime
                v-if="type === 'time'"
                ref="time"
                v-validate="validationRules"
                :name="name"
                :input-id="id"
                v-model="newValue"
                @input="$emit('input', value)"
                class="time"
                input-class="time-input"
                type="time"
                :placeholder="placeholder"
        />

        <!-- Error message if it should be shown, when setting a name attribute we see this as showing a error -->
        <!-- IMPORTANT: These do not contain the '$validator.errors', if you want to show custom errors
             you can either:
             - add the <error-container> component and use '$validator.errors.add({...})'
             or
             - use add to the 'errors' below, like this '$refs['NAME'].$validator.errors.items.push({...})' -->
        <div v-if="(name && showErrors && $validator.errors)" class="error">
            <p v-for="(error, index) in $validator.errors.items" :class="error.class ? error.class : ''" v-bind:key="index">
                {{ error.msg }}
            </p>
        </div>
    </div>
</template>

<script>
import { Default } from '@/mixins/mixins';
import { Datetime } from 'vue-datetime';
import Textbox from '@/components/additional/customInputs/textbox';
import Password from '@/components/additional/customInputs/password';
import Textarea from '@/components/additional/customInputs/textarea';
import Upload from '@/components/additional/customInputs/upload';
import Display from '@/components/additional/customInputs/display';
import DisplayTextarea from '@/components/additional/customInputs/displayTextarea';
import DisplaySecret from '@/components/additional/customInputs/displaySecret';
import DisplaySecretSmall from '@/components/additional/customInputs/displaySecretSmall';
import Toggle from '@/components/additional/customInputs/toggle';
import Dropdown from '@/components/additional/customInputs/dropdown';
import Colorpicker from '@/components/additional/customInputs/colorpicker';
import Colorbuttons from '@/components/additional/customInputs/colorbuttons';

export default {
    name: 'customInput',
    mixins: [
        Default
    ],
    props: {
        label: String,
        validationRules: {
            type: Object,
            validator: function(value) {
                const rules = ['after', 'before']; // VeeValidate target based rules that we cannot handle without custom code
                const keys = Object.keys(value);

                const foundKeys = keys.filter((key) => {
                    return rules.indexOf(key) !== -1;
                });

                if (foundKeys.length > 0) {
                    console.error(`No custom validator for validation rule '${foundKeys[0]}'.\n` +
                            "custom-input cannot work with 'target based rules'.\n" +
                            "You will have to add a validator.extend() like we did for 'confirmed' (check customInput.vue - created())\n" +
                            "After you've done this remove this error for the particular rule.");
                    return false;
                }

                return true;
            },
            default: function() {
                return {};
            }
        },
        name: String,
        id: String,
        type: {
            type: String,
            required: true
        },
        placeholder: String,
        value: null,
        showSecret: Boolean,
        items: {
            type: Array,
            validator: function(value) {
                let _return = false;
                value.forEach((item) => {
                    Object.keys(item).forEach((key) => {
                        _return = ['value', 'label'].indexOf(key) !== -1;
                    });
                });

                if (!_return)
                    console.error('custom-input items prop invalid format, should be [{value: XXX, label: XXX}, {...}]');

                return _return;
            }
        },
        colors: Object,
        acceptExtensions: Array,
        errorMode: {
            type: String,
            default: 'auto',
            validator: function(value) {
                const possibleValues = ['auto', 'manual'];
                if (possibleValues.indexOf(value) !== -1)
                    return true;

                console.error('custom-input error-type invalid value, can only be ' + possibleValues.join(' or '));
                return false;
            }
        },
        maxlength: String,
        disabled: Boolean,
        autocomplete: {
            type: String,
            default: 'off'
        },
        minHeight: {
            type: String
        }
    },
    components: {
        Display,
        'datetime': Datetime,
        'textbox': Textbox,
        'password': Password,
        'text-area': Textarea,
        'display-area': DisplayTextarea,
        'display-secret': DisplaySecret,
        'display-secret-small': DisplaySecretSmall,
        'toggle': Toggle,
        'dropdown': Dropdown,
        'colorpicker': Colorpicker,
        'colorbuttons': Colorbuttons,
        Upload
    },
    $_veeValidate: {
        validator: 'new', // Give me my own validator scope.
        name: name
    },
    data() {
        return {
            newValue: '',
            showErrors: true, // Default true, so errors are shown even if you've never focused an input but triggered validation by for ex a button
            localErrorMode: 'auto' // 'auto' will show errors on blur and hide on focus, 'manual' will only show when told to
        };
    },
    created() {
        // Override VeeValidate rule 'confirmed', because we can't use the native one because
        // -every custom-input has it's own scope and cannot find the ref to which the args is pointing
        // So you can just use this :validationRules="{ confirmed: 'DOM_ELEM_NAME' }"
        this.$validator.extend('confirmed', {
            getMessage: field => `The ${field} field does not match`,
            validate: function(value, args) {
                const field = document.querySelector(`[name=${args[0]}]`);
                return !!(field && String(value) === field.value);
            }
        });

        this.newValue = this.value;
        this.localErrorMode = this.errorMode;
        if (this.localErrorMode === 'manual') this.showErrors = false;
    },
    watch: {
        value: function(newVal, oldVal) {
            // Delete our own errors whenever we type something (value changes)
            this.$validator.errors.clear();
            if (newVal !== oldVal)
                this.newValue = newVal;
        },
        newValue: function(newVal, oldVal) {
            // Emit input event so $validator will validate the value
            if (newVal !== oldVal)
                this.$emit('input', newVal);
        }
    },
    computed: {
        acceptExtensionsString: {
            get() {
                return this.acceptExtensions.join();
            }
        }
    },
    methods: {
        toggleErrors: function(visible) {
            this.showErrors = visible;
        }
    }
};
</script>

<style lang="scss">
    .custom-input {
        .error {
            p {
                color: red;
                font-size: .8em;
            }

            .warning {
                color: orange;
            }
        }
    }
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    .custom-input {
        min-height: max-content;
        padding-bottom: 20px;

        label {
            margin: 0;
        }
    }
</style>
